import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { mergePrefs, type UserPrefs, type UserPrefsResponse } from '@tradeoutlet/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

/** t297u: my preferences, kept server-side (the same on every device). */
export const PREFS_KEY = ['me', 'prefs'] as const;

export function useMyPrefs() {
  const { status } = useAuth();
  return useQuery({ queryKey: PREFS_KEY, queryFn: () => api.myPrefs(), enabled: status === 'signed_in', staleTime: 5 * 60_000 });
}

/** Optimistic: the cache changes first, then the server's answer replaces it. */
export function useUpdatePrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (set: Record<string, unknown>) => api.updateMyPrefs(set),
    onMutate: set => {
      const prev = qc.getQueryData<UserPrefsResponse>(PREFS_KEY);
      if (prev) {
        const next = mergePrefs(prev.prefs, set);
        if (next) qc.setQueryData<UserPrefsResponse>(PREFS_KEY, { ...prev, prefs: next });
      }
      return { prev };
    },
    onError: (_e, _set, ctx) => { if (ctx?.prev) qc.setQueryData(PREFS_KEY, ctx.prev); },
    onSuccess: (r: UserPrefsResponse) => qc.setQueryData(PREFS_KEY, r),
  });
}

export const seenCount = (p: UserPrefs | undefined) => Object.keys(p?.seen_tips ?? {}).length;
